/* eslint-disable @typescript-eslint/member-ordering */
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import Big from 'big.js';
import dayjs from 'dayjs';
import { Observable, Subject, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { interpolateParams } from 'src/app/shared/utilities/interpolate-params';
import { combine } from 'src/app/shared/utilities/route';
import { ContractLengthWithBookingWeeksRequest } from '../models/contract-length-with-booking-weeks-request.model';
import { ContractLengthWithBookingWeeksResponse } from '../models/contract-length-with-booking-weeks-response.model';
import { MarginCalculatorBaseService } from './margin-calculator-base.service';
import { MarginCalculatorFormulaService } from './margin-calculator-formula.service';

// Day time frame 'day' shift time
const DAY_TIMEFRAME_START_HOURS = 7;
const DAY_TIMEFRAME_START_MINUTES = 0;
const DAY_TIMEFRAME_END_HOURS = 11;
const DAY_TIMEFRAME_END_MINUTES = 0;

// Evening time frame for 'evening' shift time
const EVENING_TIMEFRAME_START_HOURS = 15;
const EVENING_TIMEFRAME_START_MINUTES = 0;
const EVENING_TIMEFRAME_END_HOURS = 15;
const EVENING_TIMEFRAME_END_MINUTES = 0;

// Night time frame for 'night' shift time
const NIGHT_TIMEFRAME_START_HOURS = 19;
const NIGHT_TIMEFRAME_START_MINUTES = 0;
const NIGHT_TIMEFRAME_END_HOURS = 6;
const NIGHT_TIMEFRAME_END_MINUTES = 0;

interface TimeFrame {
    startHours: number;
    startMinutes: number;
    endHours: number;
    endMinutes: number;
}

interface WeeksCountCall {
    startDate: string;
    endDate: string;
    shifts: number;
    weeks: number;
}

interface ContractEndDateCall {
    startDate: string;
    weeks: number;
    endDate: Date;
}

interface ContractDatesCall {
    startDate: string;
    daysLength: number;
    shifts: number;
    endDate: Date;
    weeks: number;
}

@Injectable({
    providedIn: 'root'
})
export class MarginCalculatorDateService extends MarginCalculatorBaseService {
    private readonly _vacationEnabled$ = new Subject<boolean>();
    private readonly _startDate$ = new Subject<Date>();
    private readonly _endDate$ = new Subject<Date>();
    private readonly cache = {
        weeksCount: new Array<WeeksCountCall>(),
        contractEndDate: new Array<ContractEndDateCall>(),
        contractDates: new Array<ContractDatesCall>()
    };

    constructor(
        private readonly _httpClient: HttpClient,
        private readonly _marginFormulaService: MarginCalculatorFormulaService
    ) {
        super();        
    }

    get vacationEnabled$(): Observable<boolean> {
        return this._vacationEnabled$.asObservable();
    }

    set vacationEnabled(enabled: boolean) {
        this._vacationEnabled$.next(enabled);
    }

    set startDate(value: Date) {
        this._startDate$.next(value);
    }

    get startDate$(): Observable<Date> {
        return this._startDate$.asObservable();
    }

    set endDate(value: Date) {
        this._endDate$.next(value);
    }

    get endDate$(): Observable<Date> {
        return this._endDate$.asObservable();
    }

    /**
     * Get contract length in weeks.
     * Uses feature flag 'contract-length' to determine the algorithm.
     *
     * @param startDate Start date of the contract
     * @param endDate End date of the contract
     * @param shifts Shifts quantity for the contract
     * @returns Length of the contract - in weeks
     */
    getWeeksCount(startDate: string, endDate: string, shifts: number): Observable<number> {
        const existing = this.cache.weeksCount.find(
            (x) => x.endDate === endDate && x.startDate === startDate && x.shifts === shifts
        );
        if (existing) {
            return of(existing.weeks);
        }       
        const requestObservable$ = of(this._marginFormulaService.calculateWeeks(dayjs(startDate).toDate(), dayjs(endDate).toDate()));

        return requestObservable$.pipe(
            tap((weeks) => {
                this.cache.weeksCount.push({ startDate, endDate, shifts, weeks });
            })
        );
    }

    /**
     * Get contract length in weeks along with booking weeks and alt booking weeks.
     *
     * @param request ContractLengthWithBookingWeeksRequest
     * @returns ContractLengthWithBookingWeeksResponse
     */
    getContractLengthWithBookingWeeks(
        request: ContractLengthWithBookingWeeksRequest
    ): Observable<ContractLengthWithBookingWeeksResponse> {
        const url = combine(this.coreApiPath, 'margin-calculator/contract-length-with-booking-weeks');
        const params = interpolateParams(request);
        return this._httpClient.get<ContractLengthWithBookingWeeksResponse>(url, { params });
    }

    /**
     * Fetch the end date of the contract using startDate and contract length in weeks.
     * Uses feature flag 'contract-length' to determine which algorithm should be used.
     */
    getContractEndDate(startDate: string, weeks: number): Observable<Date> {
        const existing = this.cache.contractEndDate.find((x) => x.startDate === startDate && x.weeks === weeks);
        if (existing) {
            return of(existing.endDate);
        }        
        const requestObservable$ = this._httpClient.get<Date>(
                  `${this.coreApiPath}/margin-calculator/end-date?startDate=${startDate}&weeks=${weeks}`
              );
        return requestObservable$.pipe(
            tap((endDate) => {
                this.cache.contractEndDate.push({ startDate, weeks, endDate: dayjs(endDate).toDate() });
            }),
            map((response) => dayjs(response).toDate())
        );
    }

    /**
     * Get the end date of the contract and weeks length.
     * Uses feature flag 'contract-length' to determine the algorithm.
     *
     * @param startDate Start date of the contract.
     * @param daysLength Length of the contract in days.
     * @param shifts Shifts quantity for the contract.
     * @returns end date of the contract and weeks length.
     */
    getContractDates(
        startDate: string,
        daysLength: number,
        shifts: number
    ): Observable<{ endDate: Date; weeks: number }> {
        const existing = this.cache.contractDates.find(
            (x) => x.startDate === startDate && x.daysLength === daysLength && x.shifts === shifts
        );
        if (existing) {
            return of({ endDate: existing.endDate, weeks: existing.weeks });
        }
        const requestObservable$ = of({
                  endDate: dayjs(startDate)
                      .add(daysLength - 1, 'day')
                      .toDate(),
                  weeks: this._marginFormulaService.calculateWeeks(
                      new Date(startDate),
                      dayjs(startDate)
                          .add(daysLength - 1, 'day')
                          .toDate()
                  )
              });

        return requestObservable$.pipe(
            tap((response) => {
                this.cache.contractDates.push({
                    startDate,
                    daysLength,
                    shifts,
                    endDate: dayjs(response.endDate).toDate(),
                    weeks: response.weeks
                });
            }),
            map((response) => ({
                endDate: dayjs(response.endDate).toDate(),
                weeks: response.weeks
            }))
        );
    }

    getWeeks(startDate: string, endDate: string): Observable<number> {
        return this._httpClient
            .get<number>(`${this.coreApiPath}/margin-calculator/weeks?startDate=${startDate}&endDate=${endDate}`)
            .pipe(map((response) => response));
    }

    getTimeFrame(shiftTime: string): TimeFrame {
        switch (shiftTime) {
            case 'evening':
                return {
                    startHours: EVENING_TIMEFRAME_START_HOURS,
                    startMinutes: EVENING_TIMEFRAME_START_MINUTES,
                    endHours: EVENING_TIMEFRAME_END_HOURS,
                    endMinutes: EVENING_TIMEFRAME_END_MINUTES
                };
            case 'night':
                return {
                    startHours: NIGHT_TIMEFRAME_START_HOURS,
                    startMinutes: NIGHT_TIMEFRAME_START_MINUTES,
                    endHours: NIGHT_TIMEFRAME_END_HOURS,
                    endMinutes: NIGHT_TIMEFRAME_END_MINUTES
                };
            case 'day':
            default:
                return {
                    startHours: DAY_TIMEFRAME_START_HOURS,
                    startMinutes: DAY_TIMEFRAME_START_MINUTES,
                    endHours: DAY_TIMEFRAME_END_HOURS,
                    endMinutes: DAY_TIMEFRAME_END_MINUTES
                };
        }
    }

    getEndHours(startHours: number, shiftHours: number): number {
        const isPercent = this._isNumberPercent(shiftHours);

        if (Number(startHours) + Number(shiftHours) >= 24) {
            return isPercent
                ? Number(startHours) + Number(shiftHours) - 0.5 - 24
                : Number(startHours) + Number(shiftHours) - 24;
        }

        return isPercent ? Number(startHours) + Number(shiftHours) - 0.5 : Number(startHours) + Number(shiftHours);
    }

    getEndMinutes(endMinutes: number, shiftHours: number): number {
        const isPercent = this._isNumberPercent(shiftHours);

        // Since shift hours only come in either whole or whole + .5 varities.
        // i.e (7.5, 11.5, etc.)
        // Just add a half hour if the number is a percent
        const minutes = isPercent ? Number(endMinutes) + 30 : Number(endMinutes);

        return minutes > 60 ? minutes - 60 : minutes;
    }

    getDateDiffInDays(startDate: Date, endDate: Date): number {
        const start = dayjs(startDate);
        const end = dayjs(endDate);
        const diff = end.diff(start, 'day', true);
        return new Big(diff).add(1).round(0, Big.roundHalfUp).toNumber();
    }

    private _isNumberPercent(shiftHours: number): boolean {
        return Number(shiftHours) % 1 === 0.5;
    }
}
